// Navigation registry: every section of the app. The router (shell/routes.tsx), the phone tab bar,
// the "Lisää" page and the PC rail all read NAV. Each section declares its own entry next to its page,
// routes/<Section>.nav.ts (defineNav: path, icon, group, phone tab or Lisää, `order`, the page's
// loader), collected here with import.meta.glob.
// To add a section: routes/<Section>.tsx (default export), routes/<Section>.nav.ts, and its label
// in i18n nav (fi.ts / en.ts; NavId comes from those keys).
import type { IconName } from '../components/Icon';
import type { NavEntry } from './nav-entry';
import type { Messages } from '../i18n';

export type NavId = Exclude<keyof Messages['nav'], 'label' | 'more' | 'groups'>;

/**
 * The navigation groups, in order. PC rail: a quiet label over each group, except "app" (in the
 * rail foot, no label). Phone "Lisää": a heading per non-empty group, the tabs left out.
 */
export type NavGroup = 'practice' | 'learn' | 'find' | 'together' | 'app';
export const NAV_GROUPS: readonly NavGroup[] = ['practice', 'learn', 'find', 'together', 'app'];

export interface NavItem {
  id: NavId;
  path: string;
  icon: IconName;
  group: NavGroup;
  /** Phone: a bottom tab, or listed under "Lisää". */
  phone: 'tab' | 'more';
  /**
   * Study screens get the no-overscroll treatment on iOS (and no player bar, no install banner).
   * A function: only some of the section's screens are one (Kuuntele: the listening exercise).
   */
  session?: boolean | ((at: { pathname: string; state: unknown }) => boolean);
  /**
   * Only for the admin (lib/people.ts isAdmin): left out of the tab bar, Lisää and the rail for
   * the other person, and the route shows "Sivua ei löydy" to them (routes.tsx AdminOnly).
   */
  admin?: boolean;
  /**
   * The back bar's parent for this section's root (margin/BackBar.tsx), when it is not the default
   * (phone: Lisää for a section listed there; PC: none). Its sub-routes always go back to its root.
   */
  parent?: NavId;
}

const NAV_FILES = import.meta.glob<{ nav: NavEntry }>('../routes/*.nav.ts', { eager: true });

/** Every section's nav entry, in order (an id declared twice is an error). */
export const NAV_ENTRIES: readonly NavEntry[] = (() => {
  const all = Object.values(NAV_FILES).map((m) => m.nav);
  const seen = new Set<string>();
  for (const e of all) {
    if (seen.has(e.id)) throw new Error(`nav entry ${e.id} is declared twice`);
    seen.add(e.id);
  }
  return all.sort((a, b) => a.order - b.order);
})();

// In group order: the rail and Lisää list the sections in this order, and the phone tabs keep it
// (Tänään · Oppitunnit · Sanakirja · Kääntäjä · Lisää).
export const NAV: readonly NavItem[] = NAV_ENTRIES.map(({ order: _order, load: _load, ...item }) => item);

/** The sections this person sees: admin-only ones for the admin. */
export function visibleNav(admin: boolean): readonly NavItem[] {
  return NAV.filter((n) => admin || !n.admin);
}

/** Items per group, in NAV_GROUPS order; empty groups left out. */
export function groupNav(items: readonly NavItem[]): { group: NavGroup; items: NavItem[] }[] {
  return NAV_GROUPS.map((group) => ({ group, items: items.filter((n) => n.group === group) })).filter((g) => g.items.length > 0);
}

export const MORE_PATH = '/more';
/** "Asenna sovellus": linked from Lisää, the PC rail and the install banner (not a nav section). */
export const INSTALL_PATH = '/install';
